import { toNumberOrNull } from '@/lib/number';
import type { PondOverview } from '@/types/api';
import { PARAM_LABEL } from '@/lib/paramLabels';
import { formatCalMag, formatPair } from '@/lib/pairs';

type StatusColor = 'green' | 'amber' | 'red' | 'gray';

interface PondTooltipProps {
  pond: PondOverview;
  x: number;
  y: number;
  status: StatusColor;
}

const STATUS_LABELS: Record<StatusColor, { label: string; className: string }> = {
  green: { label: 'OK', className: 'bg-green-100 text-green-700' },
  amber: { label: 'Warning', className: 'bg-amber-100 text-amber-700' },
  red: { label: 'Alert', className: 'bg-red-100 text-red-700' },
  gray: { label: 'Idle', className: 'bg-gray-100 text-gray-500' },
};

function MetricRow({
  label,
  value,
  unit,
  decimals = 1,
  clear,
}: {
  label: string;
  value: number | null | undefined;
  unit?: string;
  decimals?: number;
  /** Transparency only: the water was clear to the bottom, so there is no number. No depth beside it: the overview's depth is the newest of two days, not necessarily this reading's (#396). */
  clear?: boolean;
}) {
  const display = clear ? 'Clear' : value !== null && value !== undefined ? value.toFixed(decimals) : '—';
  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900 tabular-nums">
        {display}
        {unit && value != null ? <span className="text-gray-400 font-normal">{unit}</span> : ''}
      </span>
    </div>
  );
}

function StringRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value ?? '—'}</span>
    </div>
  );
}

export function PondTooltip({ pond, x, y, status }: PondTooltipProps) {
  const cycle = pond.active_cycle;
  const reading = cycle?.latest_reading;
  const feeding = cycle?.last_feeding;

  const ph = toNumberOrNull(reading?.ph);
  const doVal = toNumberOrNull(reading?.dissolved_oxygen);
  const temp = toNumberOrNull(reading?.temperature_c);
  const sal = toNumberOrNull(reading?.salinity_ppt ?? null);
  const nh3 = toNumberOrNull(reading?.ammonia_ppm ?? null);
  const no2 = toNumberOrNull(reading?.nitrite_ppm ?? null);
  // alkalinity_ppm since mig 032; am/pm only on rows that predate it (#205).
  const alk = formatPair(reading?.alkalinity_ppm ?? reading?.alkalinity_am ?? reading?.alkalinity_pm ?? null, reading?.alkalinity_2);
  const depth = toNumberOrNull(reading?.depth_m ?? null);
  const trn = toNumberOrNull(reading?.transparency_cm ?? null);
  const feedAmt = toNumberOrNull(feeding?.feed_amount_kg ?? null);

  const statusInfo = STATUS_LABELS[status];
  const alertCount = pond.alert_count ?? 0;

  // Flip tooltip below if too close to top
  const flipBelow = y < 200;

  return (
    <div
      className="absolute z-50 pointer-events-none"
      style={{
        left: x,
        top: flipBelow ? y + 110 : y,
        transform: flipBelow
          ? 'translate(-50%, 0)'
          : 'translate(-50%, -100%) translateY(-12px)',
      }}
    >
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2.5 min-w-[220px] text-xs">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="font-semibold text-gray-900 text-sm">{pond.name}</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusInfo.className}`}>
            {statusInfo.label}
          </span>
        </div>

        {cycle && (
          <div className="text-gray-500 mb-2">DOC {cycle.doc}</div>
        )}

        {/* Water parameters — 2 column grid */}
        {reading && (
          <div className="border-t border-gray-100 pt-1.5">
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
              <MetricRow label="pH" value={ph} />
              <MetricRow label="DO" value={doVal} unit=" mg/L" />
              <MetricRow label={PARAM_LABEL.temp} value={temp} unit="°C" />
              <MetricRow label={PARAM_LABEL.salinity} value={sal} unit=" ppt" />
              <MetricRow label={PARAM_LABEL.ammonia} value={nh3} decimals={2} unit=" ppm" />
              <MetricRow label={PARAM_LABEL.nitrite} value={no2} decimals={2} unit=" ppm" />
            </div>

            {/* Secondary parameters */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1.5 pt-1.5 border-t border-gray-50">
              <StringRow label={PARAM_LABEL.alkalinity} value={alk && `${alk} ppm`} />
              <MetricRow label={PARAM_LABEL.depth} value={depth} unit=" m" />
              <MetricRow label={PARAM_LABEL.transparency} value={trn} decimals={0} unit=" cm" clear={reading.transparency_clear_to_bottom === true} />
              <StringRow label="Color" value={reading.water_color} />
              <StringRow label={PARAM_LABEL.calmag} value={formatCalMag(reading.calcium_ppm, reading.magnesium_ppm, reading.calmag)} />
            </div>

            <div className="mt-1 text-[10px] text-gray-400">
              Last: {reading.reading_slot}
            </div>
          </div>
        )}

        {/* Feeding */}
        {feeding && (
          <div className="mt-1.5 pt-1.5 border-t border-gray-100">
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">Feed</span>
              <span className="font-medium text-gray-900 tabular-nums">
                {feedAmt != null ? `${feedAmt.toFixed(1)} kg` : '—'}
                <span className="text-gray-400 font-normal"> · {feeding.feeding_slot}</span>
              </span>
            </div>
            {feeding.feed_type && (
              <div className="text-[10px] text-gray-400 text-right">{feeding.feed_type}</div>
            )}
          </div>
        )}

        {/* Alerts */}
        {alertCount > 0 && (
          <div className="mt-1.5 pt-1.5 border-t border-gray-100 text-red-600 font-medium">
            {alertCount} active alert{alertCount !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
}
